import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import type { KeyboardEvent } from 'react'
import { type UseFormReturn, useWatch } from 'react-hook-form'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import type { FormInputControlType, FormInputRegisterType } from '@/types'

import { addSupplierArticle } from '../lib/singularImportSupplierArticles'
import type { NomenclatureCreationType } from '../SingularImport.config'
import cn from './NomenclatureCreationForm.module.scss'

type SupplierArticlesFieldProps = {
  form: UseFormReturn<NomenclatureCreationType>
  isOrganisationSelected: boolean
}

const EMPTY_ARTICLES: string[] = []

function SupplierArticlesField({
  form,
  isOrganisationSelected,
}: SupplierArticlesFieldProps) {
  const { control, register, formState, getValues, setValue, setError } = form
  const articles =
    useWatch({ control, name: 'supplierArticles' }) ?? EMPTY_ARTICLES

  const saveArticles = (next: string[]) =>
    setValue('supplierArticles', next, {
      shouldDirty: true,
      shouldValidate: true,
    })

  const addDraft = () => {
    const result = addSupplierArticle(
      articles,
      getValues('supplierArticleDraft') ?? ''
    )
    if ('error' in result) {
      setError('supplierArticleDraft', { message: result.error })
      return false
    }
    saveArticles(result.articles)
    setValue('supplierArticleDraft', '')
    form.clearErrors('supplierArticleDraft')
    return true
  }

  const editArticle = (index: number) => {
    const hasDraft = (getValues('supplierArticleDraft') ?? '').trim() !== ''
    if (hasDraft && !addDraft()) return
    const current = getValues('supplierArticles') ?? []
    setValue('supplierArticleDraft', current[index])
    saveArticles(current.filter((_, itemIndex) => itemIndex !== index))
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    addDraft()
  }

  return (
    <div className={cn.supplierArticles}>
      <div className={cn.fieldWithAction}>
        <AtomFormInput
          layout="stacked"
          location="singularNomenclatureImport"
          name="supplierArticleDraft"
          title="Артикулы поставщика"
          placeholder="Введите артикул и нажмите +"
          isOrganisationSelected={isOrganisationSelected}
          errors={formState.errors}
          control={control as FormInputControlType}
          register={register as FormInputRegisterType}
          handleInputKeyDown={handleKeyDown}
        />
        <AtomButton
          variant="secondary"
          shape="circle"
          icon={<PlusOutlined />}
          aria-label="Добавить артикул поставщика"
          title="Добавить артикул поставщика"
          className={cn.actionButton}
          onClick={addDraft}
        />
      </div>
      {articles.length > 0 && (
        <ul className={cn.articles} aria-label="Артикулы поставщика">
          {articles.map((article, index) => (
            <li key={article}>
              <button
                type="button"
                className={cn.article}
                title="Изменить артикул"
                onClick={() => editArticle(index)}
              >
                {article}
                <EditOutlined className={cn.articleIcon} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      {formState.errors.supplierArticles?.message && (
        <span role="alert" className={cn.error}>
          {formState.errors.supplierArticles.message}
        </span>
      )}
    </div>
  )
}

export default SupplierArticlesField
